import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Download, FileText, Calendar, Shield, 
  Newspaper, Building2, ChevronDown, ChevronUp 
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api, { getApiBaseUrl } from '@/lib/api';

export default function EditionPage() {
  const { edicaoId } = useParams();
  const [edicao, setEdicao] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [expandedPubs, setExpandedPubs] = React.useState({});

  React.useEffect(() => {
    fetchEdicao();
  }, [edicaoId]);

  const fetchEdicao = async () => {
    try {
      const response = await api.get(`/edicoes/${edicaoId}`);
      setEdicao(response.data);
    } catch (error) {
      console.error('Error fetching edicao:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getCategoryBadgeClass = (cat) => {
    switch (cat) {
      case 'Executivo': return 'bg-[#B91C1C] text-white';
      case 'Legislativo': return 'bg-[#1D4ED8] text-white';
      case 'Terceiro Setor': return 'bg-[#059669] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const toggleExpand = (pubId) => {
    setExpandedPubs(prev => ({
      ...prev,
      [pubId]: !prev[pubId]
    }));
  };

  // Group publications by category
  const pubsByCategory = React.useMemo(() => {
    if (!edicao?.publicacoes) return {};
    return edicao.publicacoes.reduce((acc, pub) => {
      const cat = pub.categoria || 'Outros';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(pub);
      return acc;
    }, {});
  }, [edicao]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!edicao) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Newspaper className="mx-auto mb-4 text-gray-300" size={64} />
          <h1 className="font-heading text-2xl font-bold mb-4">Edição não encontrada</h1>
          <Link to="/">
            <Button>Voltar ao início</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="edition-page">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft size={20} className="mr-2" />
          Voltar às edições
        </Link>

        {/* Header */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-3xl font-bold">
                    Edição nº {edicao.numero_edicao}/{edicao.ano}
                  </h1>
                  <Badge className={edicao.status === 'publicado' ? 'bg-green-600' : 'bg-yellow-600'}>
                    {edicao.status}
                  </Badge>
                </div>
                
                <div className="flex flex-wrap items-center gap-4 text-gray-600">
                  <span className="flex items-center gap-2">
                    <Calendar size={16} />
                    {formatDate(edicao.data_publicacao)}
                  </span>
                  <span className="flex items-center gap-2">
                    <FileText size={16} />
                    {edicao.publicacoes?.length || 0} publicação(ões)
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Shield size={14} className="text-green-600" />
                  <span className="text-gray-600">Código de verificação:</span>
                  <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm">
                    {edicao.codigo_verificacao}
                  </code>
                </div>
              </div>

              <Button 
                className="bg-[#B91C1C] hover:bg-[#991B1B]"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${getApiBaseUrl()}/api/edicoes/${edicao.edicao_id}/pdf`;
                  link.download = `DEMOAC_Edicao_${edicao.numero_edicao}_${edicao.ano}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={18} className="mr-2" />
                Baixar PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Publications by Category */}
        {Object.entries(pubsByCategory).map(([categoria, pubs]) => (
          <div key={categoria} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Badge className={getCategoryBadgeClass(categoria) + ' text-lg px-4 py-1'}>
                {categoria}
              </Badge>
              <span className="text-gray-500">({pubs.length} publicação(ões))</span>
            </div>

            <div className="space-y-4">
              {pubs.map((pub) => (
                <Card key={pub.publicacao_id} className="pub-card" data-testid={`pub-${pub.publicacao_id}`}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{pub.subcategoria}</Badge>
                          {pub.numero && (
                            <span className="text-sm text-gray-500">{pub.numero}</span>
                          )}
                        </div>
                        <h3 className="font-heading font-bold text-lg">{pub.titulo}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          <Building2 size={14} className="inline mr-1" />
                          {pub.orgao}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleExpand(pub.publicacao_id)}
                      >
                        {expandedPubs[pub.publicacao_id] ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </Button>
                    </div>

                    {/* Text content */}
                    <div className={`mt-4 ${expandedPubs[pub.publicacao_id] ? '' : 'line-clamp-4'}`}>
                      <div 
                        className="text-gray-700 dark:text-gray-300 prose prose-sm max-w-none pub-content"
                        dangerouslySetInnerHTML={{ __html: pub.texto }}
                      />
                    </div>

                    {pub.texto && pub.texto.length > 300 && (
                      <Button 
                        variant="link" 
                        className="text-[#B91C1C] p-0 h-auto mt-2"
                        onClick={() => toggleExpand(pub.publicacao_id)}
                      >
                        {expandedPubs[pub.publicacao_id] ? 'Ver menos' : 'Ver mais'}
                      </Button>
                    )}

                    {/* Verification code */}
                    <div className="mt-4 pt-4 border-t flex items-center gap-2 text-sm text-gray-500">
                      <Shield size={14} />
                      <span>Código:</span>
                      <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs">
                        {pub.codigo_verificacao}
                      </code>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {(!edicao.publicacoes || edicao.publicacoes.length === 0) && (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <FileText className="mx-auto mb-4 text-gray-300" size={48} />
              <p>Esta edição não possui publicações.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
